import dayjs, { type Dayjs } from "dayjs";

/** Matches server `StatisticsKyselyService.DAILY_TREND_MAX_DAYS`. */
export const REGISTRATION_TRACKING_MAX_DAYS = 120;

export function registrationTrackingDefaultDay(): Dayjs {
  return dayjs().startOf("day");
}

export const registrationTrackingDefaultFrom = registrationTrackingDefaultDay;
export const registrationTrackingDefaultTo = registrationTrackingDefaultDay;

export type RegistrationTrackingStoredDates = {
  dateFrom: string;
  dateTo: string;
};

export function registrationTrackingStorageKey(organizationId: string): string {
  return `registration-tracking-dates-${organizationId}`;
}

export function parseStoredRegistrationDates(
  stored: RegistrationTrackingStoredDates | null | undefined,
): { dateFrom: Dayjs; dateTo: Dayjs } | null {
  if (!stored?.dateFrom || !stored?.dateTo) return null;
  const dateFrom = dayjs(stored.dateFrom, "YYYY-MM-DD", true);
  const dateTo = dayjs(stored.dateTo, "YYYY-MM-DD", true);
  if (!dateFrom.isValid() || !dateTo.isValid()) return null;
  if (validateRegistrationDateRange(dateFrom, dateTo)) return null;
  return { dateFrom, dateTo };
}

export function getInitialRegistrationDates(
  organizationId: string | undefined,
): { dateFrom: Dayjs; dateTo: Dayjs } {
  if (organizationId && typeof sessionStorage !== "undefined") {
    try {
      const raw = sessionStorage.getItem(
        registrationTrackingStorageKey(organizationId),
      );
      if (raw) {
        const parsed = parseStoredRegistrationDates(
          JSON.parse(raw) as RegistrationTrackingStoredDates,
        );
        if (parsed) return parsed;
      }
    } catch {
      // ignore corrupt storage
    }
  }
  return {
    dateFrom: registrationTrackingDefaultDay(),
    dateTo: registrationTrackingDefaultDay(),
  };
}

export function persistRegistrationDates(
  organizationId: string,
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
): void {
  if (
    !dateFrom?.isValid() ||
    !dateTo?.isValid() ||
    validateRegistrationDateRange(dateFrom, dateTo)
  ) {
    return;
  }
  const payload: RegistrationTrackingStoredDates = {
    dateFrom: dateFrom.format("YYYY-MM-DD"),
    dateTo: dateTo.format("YYYY-MM-DD"),
  };
  sessionStorage.setItem(
    registrationTrackingStorageKey(organizationId),
    JSON.stringify(payload),
  );
}

/** Returns a Hebrew error message, or null when the range is valid. */
export function validateRegistrationDateRange(
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
): string | null {
  if (!dateFrom?.isValid() || !dateTo?.isValid()) {
    return "יש לבחור תאריך התחלה וסיום תקינים";
  }
  if (dateFrom.isAfter(dateTo, "day")) {
    return "תאריך ההתחלה חייב להיות לפני תאריך הסיום";
  }
  const spanDays = dateTo.diff(dateFrom, "day") + 1;
  if (spanDays > REGISTRATION_TRACKING_MAX_DAYS) {
    return `טווח התאריכים גדול מדי (מקסימום ${REGISTRATION_TRACKING_MAX_DAYS} ימים)`;
  }
  return null;
}

export function clampDateToOnFromChange(
  nextFrom: Dayjs | null,
  dateTo: Dayjs | null,
): Dayjs | null {
  if (!nextFrom?.isValid() || !dateTo?.isValid()) {
    return dateTo;
  }
  if (nextFrom.isAfter(dateTo, "day")) {
    return nextFrom;
  }
  return dateTo;
}
